import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import Hero from '../../components/marketing/Hero';
import StepList from '../../components/marketing/StepList';
import TrustBadges from '../../components/marketing/TrustBadges';
import FaqAccordion from '../../components/marketing/FaqAccordion';
import CtaBanner from '../../components/marketing/CtaBanner';
import Link from '../../components/Link';
import s from './HowItWorks.css';

// COMPLIANCE: the two booking modes below must stay consistent with
// besthand-api/docs/booking-flows-LOCKED.md (Mode A broadcast / Mode B preferred). Do not contradict.
const CHOOSE_STEPS = [
  { title: 'Pick a service & time', text: 'Tell us the job and when you need it. It takes about a minute.' },
  // COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod.
  { title: 'We match you with a vetted pro', text: 'We broadcast your request to nearby background-checked pros.' },
  { title: 'Track them live', text: 'The first available pro accepts and you follow their arrival in real time.' },
];
const BROWSE_STEPS = [
  { title: 'Browse available pros', text: 'Compare nearby pros, their ratings, and their upfront prices.' },
  { title: 'Pick the one you want', text: 'Choose your preferred pro and request your time slot.' },
  { title: 'They confirm', text: 'If your pick is unavailable, we auto-find an equivalent pro at the same rate.' },
];

// LARGE PROJECTS (Renovations & multi-day jobs). COMPLIANCE / LEGAL_REVIEW_REQUIRED: the
// "verified, insured" company claim and escrow/end-to-end language are marketing copy backed by the
// company KYB/license/insurance gate + milestone-escrow flow; must be reviewed by counsel before prod.
const PROJECT_STEPS = [
  { title: 'Describe your project', text: 'Tell us about your renovation or large home-improvement project — you can get quotes without an upfront site visit.' },
  { title: 'Get quotes from verified companies', text: 'Licensed, insured, and background-checked companies review your project and send you competitive quotes to compare.' },
  { title: 'Fund milestones in escrow', text: 'Choose your company and pay securely through BestHand. Your money is held in escrow and released milestone by milestone, only as each stage of work is approved.' },
  { title: 'We track it end to end', text: 'From quote and contract to milestones, payments, and final sign-off, BestHand oversees the entire project — so you’re protected from start to finish.' },
];

const AFTER = [
  { icon: 'M12 8v4l3 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z', title: 'Track in real time', text: 'Watch your pro head your way and know exactly when they’ll arrive.' },
  { icon: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z', title: 'Chat in-app', text: 'Message your pro directly — no need to share your phone number.' },
  { icon: 'M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM3 15l5-5 4 4 3-3 6 6', title: 'Before & after photos', text: 'Your pro documents the work so you can see the results.' },
  { icon: 'M2 10h20M6 15h4m8-9H6a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h12a4 4 0 0 0 4-4V10a4 4 0 0 0-4-4Z', title: 'Pay securely in-app', text: 'Cashless, card-on-file payments — you only pay when the job is done.' },
  { icon: 'm12 17.3-6.2 3.7 1.6-7L2 9.2l7.1-.6L12 2l2.9 6.6 7.1.6-5.4 4.8 1.6 7z', title: 'Leave a review', text: 'Rate your pro and help the next customer book with confidence.' },
];

const TRUST = [
  // COMPLIANCE / LEGAL_REVIEW_REQUIRED: "$5,000" damage-protection coverage claim — the exact amount,
  // eligibility, and wording must be confirmed by counsel + insurer policy terms before prod.
  { icon: 'M12 2 4 5v6c0 5 3.4 8.5 8 11 4.6-2.5 8-6 8-11V5z M9 12l2 2 4-4', title: 'Peace of Mind Protection', text: 'Every small job is backed by BestHand’s damage protection — eligible property damage caused by a pro is covered up to $5,000, subject to the program terms. Our pros carry their own insurance too, so you’re protected on two levels.' },
  { icon: 'M12 2 4 6v6c0 5 3.4 8 8 10 4.6-2 8-5 8-10V6z', title: 'Identity-verified pros', text: 'Every pro confirms their identity before they can take jobs.' },
  // COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod.
  { icon: 'M9 12l2 2 4-4M12 2 4 6v6c0 5 3.4 8 8 10 4.6-2 8-5 8-10V6z', title: 'Verification step', text: 'A paid verification step is part of pro onboarding.' },
  { icon: 'M2 10h20M6 15h4m8-9H6a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h12a4 4 0 0 0 4-4V10a4 4 0 0 0-4-4Z', title: 'Secure payments', text: 'Cashless, in-app payments powered by Stripe — no cash off-app.' },
  { icon: 'M21 11.5a8.4 8.4 0 0 1-.9 3.8A8.5 8.5 0 0 1 12.5 20a8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7A8.4 8.4 0 0 1 4 11.5 8.5 8.5 0 0 1 11.5 3h.5A8.5 8.5 0 0 1 21 11z', title: 'Support that has your back', text: 'If something isn’t right, our team works with you to make it right.' },
];

// LEGAL_REVIEW_REQUIRED: booking/match-time claims ("about 60 seconds") and trust copy are marketing
// copy and must be reviewed before prod.
const FAQS = [
  { q: 'How fast can I get matched?', a: 'Many bookings get matched with a nearby pro in about 60 seconds, depending on your area and the time of day.' },
  { q: 'Can I choose my own pro?', a: 'Yes. Use “Browse pros” to compare available pros and their prices, then pick the one you want.' },
  { q: 'Do I need cash?', a: 'No. Everything is cashless — you pay securely in-app, and you only pay once the job is done.' },
  { q: 'What if my chosen pro can’t make it?', a: 'If your preferred pro becomes unavailable, we automatically find an equivalent pro at the same rate.' },
  // COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod.
  { q: 'How do I know the pro is trustworthy?', a: 'Every pro completes a background check, and you can read real customer reviews before you book.' },
  // COMPLIANCE / LEGAL_REVIEW_REQUIRED: "verified, insured" companies + escrow claim — counsel review before prod.
  { q: 'Can I get quotes for a large renovation project?', a: 'Yes. For larger renovations and multi-day projects, you can request quotes from verified, insured companies and pay securely through BestHand. Your payment is held in escrow and released in milestones as the work is completed, and we track the whole project end to end.' },
];

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to book a trusted local pro on BestHand',
    step: [
      { '@type': 'HowToStep', name: 'Pick a service and time', text: 'Tell BestHand the job and when you need it.' },
      // COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod.
      { '@type': 'HowToStep', name: 'Get matched with a vetted pro', text: 'BestHand matches you with a nearby background-checked pro.' },
      { '@type': 'HowToStep', name: 'Track, chat, and pay in-app', text: 'Follow your pro in real time, message them in-app, and pay securely once the job is done.' },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  },
];

function HowItWorks() {
  return (
    <main className={s.page}>
      {/* SECURITY: escape </script> breakouts in JSON-LD (defense-in-depth). */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026') }}
      />

      <Hero
        eyebrow="How it works"
        title="Book a trusted pro in three taps."
        subtitle="Tell us the job, pick a time, and we match you with a vetted local pro — often in under 60 seconds."
        primaryLabel="Book a pro"
        primaryTo="/category?type=all"
        secondaryLabel="Get the app"
        secondaryTo="/get-app"
        badges={[/* COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod. */ 'Background-checked pros', 'Upfront pricing', 'Secure in-app payment']}
      />

      <section className={s.sec}>
        <div className={s.wrap}>
          <h2 className={s.h2}>Two ways to book</h2>
          <p className={s.lead}>Want it handled for you, or want to choose? BestHand gives you both.</p>
          <div className={s.twoCol}>
            <div className={s.modeCard}>
              <span className={s.modeTag}>Choose for me</span>
              <StepList steps={CHOOSE_STEPS} columns={1} />
              <p className={s.modeNote}>You pay the platform’s flat, upfront price.</p>
            </div>
            <div className={s.modeCard}>
              <span className={`${s.modeTag} ${s.modeTagAlt}`}>Browse pros</span>
              <StepList steps={BROWSE_STEPS} columns={1} accent="green" />
              <p className={s.modeNote}>Same upfront pricing — you just pick who does the job.</p>
            </div>
          </div>
        </div>
      </section>

      <section className={`${s.sec} ${s.alt}`}>
        <div className={s.wrap}>
          <TrustBadges heading="What happens after you book" items={AFTER} />
        </div>
      </section>

      <section className={s.sec}>
        <div className={s.wrap}>
          <h2 className={s.h2}>Planning a bigger renovation?</h2>
          {/* COMPLIANCE / LEGAL_REVIEW_REQUIRED: "verified, insured" + escrow claims — counsel review before prod. */}
          <p className={s.lead}>
            For large home-improvement and renovation projects, you can request quotes from verified,
            insured companies and pay securely through BestHand. Payments are held in escrow and released
            in milestones as the work is completed, and we track the entire project from start to finish.
          </p>
          <StepList steps={PROJECT_STEPS} columns={2} />
          <div className={s.faqMore}>
            <Link to="/post-a-project" className={s.faqLink}>Get project quotes →</Link>
          </div>
        </div>
      </section>

      <section className={s.proBand}>
        <div className={s.wrap}>
          <div className={s.proInner}>
            <div>
              <h2 className={s.proTitle}>Are you a pro? Earn on your schedule.</h2>
              <p className={s.proText}>Join thousands of pros getting paid jobs sent straight to their phone.</p>
            </div>
            <Link to="/become-a-pro" className={s.proBtn}>Become a Pro</Link>
          </div>
        </div>
      </section>

      <section className={`${s.sec} ${s.alt}`}>
        <div className={s.wrap}>
          <TrustBadges heading="Trust built into every booking" items={TRUST} />
        </div>
      </section>

      <section className={s.sec}>
        <div className={s.wrap}>
          <FaqAccordion heading="Booking questions" items={FAQS} />
          <div className={s.faqMore}>
            <Link to="/faq" className={s.faqLink}>See all questions →</Link>
          </div>
        </div>
      </section>

      {/* COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod. */}
      <CtaBanner
        title="Ready to get it done?"
        subtitle="Book a background-checked pro in minutes — upfront pricing, secure payment, satisfaction backed."
        primaryLabel="Book a pro"
        primaryTo="/category?type=all"
        secondaryLabel="Get the app"
        secondaryTo="/get-app"
      />
    </main>
  );
}

export default withStyles(s)(HowItWorks);
